import { useSelector } from 'react-redux';
import type { Set } from 'immutable';

import { documentsByIdSelector } from 'domain/documents';
import { getCurrentSubtype } from 'domain/documents/helpers';
import CONST from 'domain/documents/constants';

// DA-19719: a doctype tab / sub-type chip that equals the document's CURRENT type is not a valid
// drop target — the drag is rejected, nothing is written. Same rule the More menu already applies
// by showing the currently-assigned sub-type disabled (da-19295 req6), on a different surface.
// Read off `tags`, NOT the record's `doctype`: DocumentFactory defaults `doctype` to 'general', so
// a document carrying no _T_ tag would masquerade as General and wrongly block the General tab.
// The Financial tab blocks EVERY Financial document, sub-typed or not, exactly like General blocks
// General. `_T_FIN` lands a General document on Unclassified — that drop stays allowed and is how a
// document becomes Financial — but the backend does not let an already-Financial document go back
// to unrecognised (found in QA), so dropping one on the Financial tab would report success and
// change nothing. Do not reintroduce a sub-type carve-out here unless that backend rule changes.
export const hasWriteType = (tags: Set<string> | null | undefined, writeType: string) => {
  if (!tags) return false;
  if (writeType === CONST.tags.financial || writeType === CONST.tags.general) return tags.contains(writeType);
  return getCurrentSubtype(tags) === writeType;
};

// A drop is allowed while at least ONE document in the payload actually changes type; a target is
// rejected only when EVERY selected document already has it. `ids` must be the exact set the drop
// handler sends, or the guard and the write disagree.
export const useCanReclassify = (writeType: string) => {
  const documentsById = useSelector(documentsByIdSelector);
  return (ids: string[]) => ids.some((id) => !hasWriteType(documentsById.get(id)?.tags, writeType));
};
